import { useEffect, useRef } from "react";
import { env } from "./env";

export interface LiveChange {
  kind: "order" | "instance" | "recording" | "session" | "patient";
  orderId: string | null;
  at: string;
}

/**
 * Hold one Server-Sent Events stream to Jupiter (GET /events) and call onChange for every change in this practice,
 * plus once on every (re)connect so a tab that was offline catches up. EventSource cannot send a bearer token, so
 * this reads the stream with fetch. Reconnects with backoff after any drop; closes on unmount.
 */
export function useLive(token: string | null, onChange: (e: LiveChange | { kind: "connect" }) => void): void {
  const cb = useRef(onChange);
  cb.current = onChange;
  useEffect(() => {
    if (!token) return;
    const ctl = new AbortController();
    let delay = 1000;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const run = async () => {
      try {
        const res = await fetch(`${env.apiUrl}/events`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" },
          signal: ctl.signal,
        });
        if (!res.ok || !res.body) throw new Error(String(res.status));
        delay = 1000;
        cb.current({ kind: "connect" });
        const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
        let buf = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += value;
          let i: number;
          while ((i = buf.indexOf("\n\n")) >= 0) {
            const frame = buf.slice(0, i);
            buf = buf.slice(i + 2);
            const data = frame
              .split("\n")
              .filter((l) => l.startsWith("data:"))
              .map((l) => l.slice(5).trim())
              .join("\n");
            if (data) {
              try {
                cb.current(JSON.parse(data));
              } catch {
                /* ignore a malformed frame */
              }
            }
          }
        }
      } catch {
        /* dropped or refused: reconnect below */
      }
      if (ctl.signal.aborted) return;
      timer = setTimeout(run, delay);
      delay = Math.min(delay * 2, 15_000);
    };
    run();
    return () => {
      ctl.abort();
      if (timer) clearTimeout(timer);
    };
  }, [token]);
}
